import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Afternoon,
  Friday,
  Morning,
  Night,
  Saturday,
  Sunday,
  Thursday,
  Tuesday,
  Wednesday,
  Monday,
  NoDay,
  NoTime,
} from '../../../components/edd/TimeAndDay/timeAndDay';

const ONBOARDING_FEATURE_FLAG = 'EDD_ONBOARDING';

export const setFeatureFlag = async (featureFlag = ONBOARDING_FEATURE_FLAG, value = false) => {
  const response = await fetch('/cms/data/v1/edd/set-feature-flag', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ featureFlag, value }),
  });

  if (response.ok) {
    const result = await response.json();

    return { success: true, ...result };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }
  }
};

export const fetchFiles = async (limit = 10, page = 1) => {
  const response = await fetch(`/cms/data/v1/edd/fetch-files?limit=${limit}&page=${page}`, {
    method: 'GET',
  });

  if (response.ok) {
    const result = await response.json();
    const transformedData = result.rows.map(transformBackendData);

    return {
      success: true,
      transformedData,
      totalNumberOfRows: result.totalNumberOfRows,
      showOnboarding: result.onBoardingFeatureFlag,
    };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
};

export async function filterFiles( // NOSONAR
  fileTypes,
  validationStatuses,
  filterSearchValue,
  importStatuses,
  uploadedDate,
  importedDate,
  sortOrder = 'asc',
  sortColumn = 'uploadedDate',
  limit = 10,
  page = 1,
) {
  let requestString = `/cms/data/v1/edd/filter-files?limit=${limit}&page=${page}`;

  if (fileTypes.length > 0) {
    fileTypes.forEach(el => {
      requestString += `&fileTypes[]=${el}`;
    });
  }

  if (validationStatuses.length > 0) {
    validationStatuses.forEach(el => {
      requestString += `&validationStatuses[]=${el}`;
    });
  }

  if (filterSearchValue) {
    requestString += `&fileName=${filterSearchValue}`;
  }

  if (importStatuses.length > 0) {
    importStatuses.forEach(el => {
      requestString += `&importStatuses[]=${el}`;
    });
  }

  if (uploadedDate) {
    requestString += `&uploadedDate=${uploadedDate}`;
  }

  if (importedDate) {
    requestString += `&importedDate=${importedDate}`;
  }

  if (sortOrder) {
    requestString += `&sortOrder=${sortOrder}`;
  }

  if (sortColumn) {
    requestString += `&sortColumn=${sortColumn}`;
  }

  const response = await fetch(requestString, {
    method: 'GET',
  });

  if (response.ok) {
    const result = await response.json();
    const transformedData = result.rows.map(transformBackendData);

    return { transformedData, totalNumberOfRows: result.totalNumberOfRows, success: true };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
}

export const reuploadFile = async (newFile, id) => {
  const formData = new FormData();

  formData.append('file', newFile);
  formData.append('id', id);
  const response = await fetch('/cms/data/v1/edd/re-upload', {
    method: 'POST',
    body: formData,
  });

  if (response.ok) {
    const result = await response.json();
    const updatedRow = result.updatedRow;
    const csvData = result.csvData;
    const titles = Object.keys(csvData[0]);
    const columns = {};
    const numberColumns = {
      No: [...new Array(csvData.length).keys()].map(el => ({ id: uuidv4(), value: `${el + 1}` })),
    };

    titles.forEach(el => {
      csvData.forEach(row => {
        if (columns[el]) {
          columns[el].push({ id: uuidv4(), value: row[el] });
        } else {
          columns[el] = [{ id: uuidv4(), value: row[el] }];
        }
      });
    });

    if (columns['']) {
      delete columns[''];
    }

    return {
      success: true,
      updatedRow: getFileDetailsSpecifics(updatedRow),
      columns: { ...numberColumns, ...columns },
    };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
};

export const uploadFiles = async (newFiles, previousFiles, fileNames) => {
  try {
    const formData = new FormData();

    newFiles.forEach((element, index) => {
      formData.append('files', element);
      formData.append('fileNames[]', fileNames[index].trim());
    });

    const response = await fetch('/cms/data/v1/edd/upload-files', {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      const result = await response.json();
      const transformedSetofFiles = result.allFilesRows.map(transformBackendData);

      return {
        success: true,
        allFiles: transformedSetofFiles,
        totalNumberOfRows: result.totalNumberOfRows,
      };
    }

    if (response.status === 403) {
      if (window) {
        window.location.reload(true);
      }

      return;
    }

    const error = await response.json();
    const transformedSetofFiles = error.allFilesRows.map(transformBackendData);

    return {
      success: false,
      ...error,
      allFiles: transformedSetofFiles,
      totalNumberOfRows: error.totalNumberOfRows,
    };
  } catch (error) {
    return { success: false, ...error };
  }
};

export const validateFileBeforeUpload = files => {
  if (files.length === 0) {
    return { valid: false, message: 'You must upload a file' };
  }

  const csvFiles = files.filter(file => file.name.toLowerCase().endsWith('.csv'));
  const excelFiles = files.filter(
    file => file.name.toLowerCase().endsWith('.xls') || file.name.toLowerCase().endsWith('.xlsx'),
  );
  const isOnlyCSV = csvFiles.length === files.length;
  const isSingleExcel = excelFiles.length === 1 && files.length === 1;

  if (!(isOnlyCSV || isSingleExcel)) {
    return {
      valid: false,
      message:
        "You can upload either multiple csv files or a single excel file but you can't mix both",
    };
  }

  return { valid: true, mesage: 'file is valid' };
};

export const importFiles = async filesToImport => {
  const ids = filesToImport.map(file => file.id);
  const response = await fetch('/cms/data/v1/edd/import-files', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });

  if (response.ok) {
    const result = await response.json();
    const transformedSetofFiles = result.allFilesRows.map(transformBackendData);

    return {
      success: true,
      allFiles: transformedSetofFiles,
      totalNumberOfRows: result.totalNumberOfRows,
      importedFiles: result.importFilesResult.map(el => getFileDetailsSpecifics(el.importedFile)),
    };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
};

export const deployFiles = async filesToDeploy => {
  const ids = filesToDeploy.map(file => file.id);
  const response = await fetch('/cms/data/v1/edd/deploy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });

  if (response.ok) {
    const result = await response.json();
    const transformedSetofFiles = result.allFilesRows.map(transformBackendData);

    return {
      success: true,
      allFiles: transformedSetofFiles,
      totalNumberOfRows: result.totalNumberOfRows,
      importedFiles: result.deployedFilesResult.map(el => getFileDetailsSpecifics(el.deployedFile)),
    };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
};

export const cancelScheduledImport = async fileDetails => {
  const ids = fileDetails.map(el => el.id);
  const response = await fetch('/cms/data/v1/edd/cancel-schedule', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });

  if (response.ok) {
    const result = await response.json();
    const transformedSetofFiles = result.allFilesRows.map(transformBackendData);
    const filesScheduledForCancel = result.filesScheduledCancel.map(getFileDetailsSpecifics);

    return { success: true, allFiles: transformedSetofFiles, filesScheduledForCancel };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
};

export const scheduleImport = async (files, dates) => {
  const ids = files.map(file => file.id);
  const sqlDateFormats = dates.map(el => {
    return getCurrentDateTimeInUTC(el);
  });
  const response = await fetch('/cms/data/v1/edd/schedule-import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, dates: sqlDateFormats }),
  });

  if (response.ok) {
    const result = await response.json();
    const transformedSetofFiles = result.allFilesRows.map(transformBackendData);

    return {
      success: true,
      result,
      allFiles: transformedSetofFiles,
      filesScheduledForImport: result.filesScheduledForImport.map(el =>
        getFileDetailsSpecifics(el),
      ),
    };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
};

export const cancelScheduledDeploy = async fileDetails => {
  const ids = fileDetails.map(el => el.id);
  const response = await fetch('/cms/data/v1/edd/cancel-scheduled-deployment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });

  if (response.ok) {
    const result = await response.json();
    const transformedSetofFiles = result.allFilesRows.map(transformBackendData);
    const filesScheduledForCancel = result.filesScheduledCancel.map(getFileDetailsSpecifics);

    return { success: true, allFiles: transformedSetofFiles, filesScheduledForCancel };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
};

export const scheduleDeploy = async (files, dates) => {
  const ids = files.map(file => file.id);
  const sqlDateFormats = dates.map(el => {
    return getCurrentDateTimeInUTC(el);
  });
  const response = await fetch('/cms/data/v1/edd/schedule-deployment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, dates: sqlDateFormats }),
  });

  if (response.ok) {
    const result = await response.json();
    const transformedSetofFiles = result.allFilesRows.map(transformBackendData);

    return {
      success: true,
      result,
      allFiles: transformedSetofFiles,
      filesScheduledForDeploy: result.filesScheduledForDeployment.map(el =>
        getFileDetailsSpecifics(el),
      ),
    };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
};

export const validateFiles = async filesToValidate => {
  const ids = filesToValidate.map(file => file.id);
  const response = await fetch('/cms/data/v1/edd/validate-files', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });

  if (response.ok) {
    const result = await response.json();
    const transformedSetofFiles = result.allFilesRows.map(transformBackendData);

    return {
      success: true,
      allFiles: transformedSetofFiles,
      totalNumberOfRows: result.totalNumberOfRows,
      validatedFiles: result.validatePromiseResult.map(el =>
        getFileDetailsSpecifics(el.validatedFile),
      ),
    };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return { success: false, ...error };
};

export const downloadFiles = async filesToDownload => {
  const ids = filesToDownload.map(file => file.id);
  const downloadFilePromises = ids.map(async (el, index) => {
    const response = await fetch(`/cms/data/v1/edd/download-files?id=${el}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filesToDownload[index].fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  });

  await Promise.all(downloadFilePromises);

  return { success: true };
};

export const deleteFiles = async filesToDelete => {
  const ids = filesToDelete.map(file => file.id);
  const response = await fetch('/cms/data/v1/edd/delete-files', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });

  if (response.ok) {
    const result = await response.json();
    const transformedSetofFiles = result.allFilesRows.map(transformBackendData);

    return {
      success: true,
      allFiles: transformedSetofFiles,
      totalNumberOfRows: result.totalNumberOfRows,
    };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();
  const transformedSetofFiles = error.allFilesRows.map(transformBackendData);

  return {
    success: false,
    ...error,
    allFiles: transformedSetofFiles,
    totalNumberOfRows: error.totalNumberOfRows,
  };
};

export const fetchFileDetails = async index => {
  const response = await fetch(`/cms/data/v1/edd/fetch-file?id=${index}`, {
    method: 'GET',
  });

  if (response.ok) {
    const result = await response.json();
    const transformedData = getFileDetailsSpecifics(result.row);
    const csvData = result.csvData;
    const titles = Object.keys(csvData[0]);
    const columns = {};
    const numberColumns = {
      No: [...new Array(csvData.length).keys()].map(el => ({ id: uuidv4(), value: `${el + 1}` })),
    };

    titles.forEach(el => {
      csvData.forEach(row => {
        if (columns[el]) {
          columns[el].push({ id: uuidv4(), value: row[el] });
        } else {
          columns[el] = [{ id: uuidv4(), value: row[el] }];
        }
      });
    });

    if (columns['']) {
      delete columns[''];
    }

    transformedData.columns = { ...numberColumns, ...columns };

    return { ...transformedData, success: true };
  }

  if (response.status === 403) {
    if (window) {
      window.location.reload(true);
    }

    return;
  }

  const error = await response.json();

  return {
    success: false,
    title: 'Something went wrong',
    description: 'Please try again later',
    ...error,
  };
};

const fileThemes = {
  sequence: 'text-purple-500',
  schedule: 'text-[#B2794D]',
  playlist: 'text-[#C936B3]',
  assignment: 'text-[#f09b0f]',
  tag: 'text-[#f09b0f]',
};

export const bgFileTheme = {
  sequence: 'bg-purple-500',
  schedule: 'bg-[#B2794D]',
  playlist: 'bg-[#C936B3]',
  assignment: 'bg-[#f09b0f]',
  tag: 'bg-[#f09b0f]',
};

export const getFileDetailsSpecifics = fileDetail => {
  const possibleDaysComb = {
    monday: Monday,
    tuesday: Tuesday,
    wednesday: Wednesday,
    thursday: Thursday,
    friday: Friday,
    saturday: Saturday,
    sunday: Sunday,
    noDay: NoDay,
  };
  const timeComponents = {
    morning: Morning,
    afternoon: Afternoon,
    night: Night,
    noTime: NoTime,
  };
  const fileType = fileDetail.file_type;
  const importDate = fileDetail.imported_date;
  const scheduledDeploymentBy = fileDetail.scheduled_deployment_by;
  const deployedBy = fileDetail.deployed_by;
  const scheduledImportDate = fileDetail.scheduled_import_date;
  const scheduledDeploymentDate = fileDetail.schedule_deploy_date;
  const deployDate = fileDetail.deploy_date;
  const uploadedDate = fileDetail.uploaded_date;
  const validationDate = fileDetail.last_validation_date;
  const error = fileDetail.validation_status.toLowerCase() === 'invalid';
  const valid = fileDetail.validation_status.toLowerCase() === 'valid';
  const importedBy = fileDetail.imported_by ? fileDetail.imported_by : 'Not yet imported';
  const validatedBy = fileDetail.validation_by;
  const uploadedBy = fileDetail.uploaded_by;
  const errors = fileDetail.errors;
  const errorCount = errors?.length;
  const noOfRows = fileDetail.file_rows;
  const importStatus = fileDetail.import_status;
  const errorsToDisplay =
    errors?.map(el => ({
      id: uuidv4(),
      title: el.action ? `${el.action} ${fileType} error` : ` ${fileType} file error`,
      descriptionParts: (el.rowErrors || []).map(rowError => ({ id: uuidv4(), value: rowError })),
    })) ?? [];
  let importDayComponent = possibleDaysComb.noDay;
  let deployDayComponent = possibleDaysComb.noDay;
  let importedDateString = 'incoming';
  let importDateTimeComponent = timeComponents.noTime;
  let importReadableTime = 'incoming';
  let validationDayComponent = possibleDaysComb.noDay;
  let validationDateString = 'incoming';
  let validationDateTimeComponent = timeComponents.noTime;
  let validationReadableTime = 'incoming';
  let scheduledImportDayComponent = possibleDaysComb.noDay;
  let scheduledDeploymentDayComponent = possibleDaysComb.noDay;
  let scheduledImportDateString = 'incoming';
  let scheduledDeploymentDateString = 'incoming';
  let scheduledImportDateTimeComponent = timeComponents.noTime;
  let scheduledDeploymentDateTimeComponent = timeComponents.noTime;
  let scheduledImportReadableTime = 'incoming';
  let scheduledDeploymentReadableTime = 'incoming';
  let uploadedDayComponent = possibleDaysComb.noDay;
  let uploadedDateString = 'incoming';
  let deployDateString = 'incoming';
  let uploadedDateTimeComponent = timeComponents.noTime;
  let deployTimeComponent = timeComponents.noTime;
  let uploadedReadableTime = 'incoming';
  let deployReadableTime = 'incoming';

  // morning: 7am to 2pm
  // afternoon: 2pm to  6pm
  // night: 6pm to 7am

  if (importDate) {
    const {
      dayName: importDayName,
      monthName: importMonthName,
      formattedDay: importFormattedDay,
      timeOfDay: importTimeOfDay,
      year: importYear,
      readableTime,
    } = getDateInfo(importDate);

    importDayComponent = possibleDaysComb[importDayName.toLowerCase()];
    importedDateString = `${importFormattedDay} ${importMonthName}, ${importYear}`;
    importDateTimeComponent = timeComponents[importTimeOfDay];
    importReadableTime = readableTime;
  }

  if (validationDate) {
    const {
      dayName: validationDayName,
      monthName: validationMonthName,
      formattedDay: validationFormattedDay,
      timeOfDay: validationTimeOfDay,
      year: validationYear,
      readableTime,
    } = getDateInfo(validationDate);

    validationDayComponent = possibleDaysComb[validationDayName.toLowerCase()];
    validationDateString = `${validationFormattedDay} ${validationMonthName}, ${validationYear}`;
    validationDateTimeComponent = timeComponents[validationTimeOfDay];
    validationReadableTime = readableTime;
  }

  if (scheduledImportDate) {
    const {
      dayName: scheduledImportDayName,
      monthName: scheduledImportMonthName,
      formattedDay: scheduledImportFormattedDay,
      timeOfDay: scheduledImportTimeOfDay,
      year: scheduledImportYear,
      readableTime,
    } = getDateInfo(scheduledImportDate);

    scheduledImportDayComponent = possibleDaysComb[scheduledImportDayName.toLowerCase()];
    scheduledImportDateString = `${scheduledImportFormattedDay} ${scheduledImportMonthName}, ${scheduledImportYear}`;
    scheduledImportDateTimeComponent = timeComponents[scheduledImportTimeOfDay];
    scheduledImportReadableTime = readableTime;
  }

  if (scheduledDeploymentDate) {
    const {
      dayName: scheduledDeploymentDayName,
      monthName: scheduledDeploymentMonthName,
      formattedDay: scheduledDeploymentFormattedDay,
      timeOfDay: scheduledDeploymentTimeOfDay,
      year: scheduledDeploymentYear,
      readableTime,
    } = getDateInfo(scheduledDeploymentDate);

    scheduledDeploymentDayComponent = possibleDaysComb[scheduledDeploymentDayName.toLowerCase()];
    scheduledDeploymentDateString = `${scheduledDeploymentFormattedDay} ${scheduledDeploymentMonthName}, ${scheduledDeploymentYear}`;
    scheduledDeploymentDateTimeComponent = timeComponents[scheduledDeploymentTimeOfDay];
    scheduledDeploymentReadableTime = readableTime;
  }

  if (uploadedDate) {
    const {
      dayName: uploadedDayName,
      monthName: uploadedMonthName,
      formattedDay: uploadedFormattedDay,
      timeOfDay: uploadedTimeOfDay,
      year: uploadedYear,
      readableTime,
    } = getDateInfo(uploadedDate);

    uploadedDayComponent = possibleDaysComb[uploadedDayName.toLowerCase()];
    uploadedDateString = `${uploadedFormattedDay} ${uploadedMonthName}, ${uploadedYear}`;
    uploadedDateTimeComponent = timeComponents[uploadedTimeOfDay];
    uploadedReadableTime = readableTime;
  }

  if (deployDate) {
    const {
      dayName: deployDayName,
      monthName: deployMonthName,
      formattedDay: deployFormattedDay,
      timeOfDay: deployTimeOfDay,
      year: deployYear,
      readableTime,
    } = getDateInfo(deployDate);

    deployDayComponent = possibleDaysComb[deployDayName.toLowerCase()];
    deployDateString = `${deployFormattedDay} ${deployMonthName}, ${deployYear}`;
    deployTimeComponent = timeComponents[deployTimeOfDay];
    deployReadableTime = readableTime;
  }

  let deployStatus;

  if (fileDetail.deploy_status) {
    if (fileDetail.deploy_status.toLowerCase() === 'nothing_to_deploy') {
      deployStatus = 'No devices found';
    } else if (fileDetail.deploy_status.toLowerCase() === 'scheduled_for_deploy') {
      deployStatus = 'Scheduled';
    } else if (fileDetail.deploy_status.toLowerCase() === 'successful') {
      deployStatus = 'Successfully Deployed';
    } else if (fileDetail.deploy_status.toLowerCase() === 'cancelled_scheduled_deployment') {
      deployStatus = 'Scheduled deployment cancelled';
    }
  }

  const fileTheme = fileThemes[fileType.toLowerCase()];

  return {
    id: fileDetail.id,
    fileName: fileDetail.file_name,
    error,
    fileType,
    importStatus,
    importDayComponent,
    validationDayComponent,
    scheduledImportDayComponent,
    uploadedDayComponent,
    importedDateString,
    validationDateString,
    scheduledImportDateString,
    uploadedDateString,
    importDateTimeComponent,
    validationDateTimeComponent,
    scheduledImportDateTimeComponent,
    uploadedDateTimeComponent,
    fileTheme,
    importReadableTime,
    validationReadableTime,
    scheduledImportReadableTime,
    scheduledDeploymentDateTimeComponent,
    scheduledDeploymentDate,
    scheduledDeploymentDateString,
    scheduledDeploymentDayComponent,
    scheduledDeploymentReadableTime,
    uploadedReadableTime,
    valid,
    importedBy,
    validatedBy,
    deployStatus,
    uploadedBy,
    errorCount,
    errorsToDisplay,
    errors,
    noOfRows,
    importDate,
    scheduledImportDate,
    validationDate,
    uploadedDate,
    scheduledDeploymentBy,
    deployedBy,
    validationBy: fileDetail.validation_by,
    deployDayComponent,
    deployReadableTime,
    deployDateString,
    deployTimeComponent,
    deployDate,
  };
};

function getLocalDateFromMySQLFormat(isoDateString) {
  return new Date(isoDateString);
}

export function getLocalDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // month is 0-based
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getDateInfo(dateString) {
  const date = getLocalDateFromMySQLFormat(dateString);

  if (Number.isNaN(date.getTime())) {
    return { error: 'Invalid date' };
  }

  // Day of the week
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = daysOfWeek[date.getDay()];
  // Month name
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const monthName = months[date.getMonth()];
  // Day of the month with ordinal suffix (1st, 2nd, 3rd, etc.)
  const dayOfMonth = date.getDate();

  const ordinalSuffix = n => {
    if (n >= 11 && n <= 13) {
      return 'th';
    }

    const lastDigit = n % 10;
    let suffix;

    if (lastDigit === 1) {
      suffix = 'st';
    } else if (lastDigit === 2) {
      suffix = 'nd';
    } else if (lastDigit === 3) {
      suffix = 'rd';
    } else {
      suffix = 'th';
    }

    return suffix;
  };

  const formattedDay = `${dayOfMonth}${ordinalSuffix(dayOfMonth)}`;
  // Year
  const year = date.getFullYear();
  // Time of day
  const hours = date.getHours();
  let timeOfDay;

  if (hours >= 7 && hours < 14) {
    timeOfDay = 'morning';
  } else if (hours >= 14 && hours < 18) {
    timeOfDay = 'afternoon';
  } else {
    timeOfDay = 'night';
  }

  const minutes = date.getMinutes();
  const readableMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const readableTime = `${((hours + 11) % 12) + 1}:${readableMinutes} ${hours >= 12 ? 'PM' : 'AM'}`;

  return { dayName, monthName, formattedDay, timeOfDay, year, readableTime };
}

export const transformBackendData = data => {
  const transformedData = {
    id: data.id,
    fileName: data.file_name,
    type: data.file_type,
    uploadedDate: data.uploaded_date,
    validationStatus: data.validation_status,
    scheduledDate: data.scheduled_import_date,
    scheduledTime: data.scheduled_import_date,

    selected: false,
    selectedTime: -2,

    importedBy: data.imported_by,
    importedDate: data.imported_date,
    uploadedBy: data.uploaded_by,
    validatedBy: data.validation_by,
    imported: data.import_status === 'imported',
    importStatus: data.import_status,
    validationDate: data.last_validation_date,
  };

  if (transformedData.uploadedDate) {
    const {
      monthName: uploadedMonthName,
      formattedDay: uploadedFormattedDay,
      year: uploadedYear,
    } = getDateInfo(transformedData.uploadedDate);
    const uploadedDateString = `${uploadedFormattedDay} ${uploadedMonthName}, ${uploadedYear}`;

    transformedData.uploadedDateString = uploadedDateString;
  }

  if (transformedData.scheduledDate) {
    const {
      monthName: scheduledImportMonthName,
      formattedDay: scheduledImportFormattedDay,
      timeOfDay: scheduledImportTimeOfDay,
      year: scheduledImportYear,
    } = getDateInfo(transformedData.scheduledDate);
    const scheduledImportDateString = `${scheduledImportFormattedDay} ${scheduledImportMonthName}, ${scheduledImportYear}`;

    transformedData.scheduledImportDateString = scheduledImportDateString;
    transformedData.scheduledTime = scheduledImportTimeOfDay;
  }

  if (transformedData.validationDate) {
    const {
      monthName: validationMonthName,
      formattedDay: validationFormattedDay,
      year: validationYear,
    } = getDateInfo(transformedData.validationDate);
    const validationDateString = `${validationFormattedDay} ${validationMonthName}, ${validationYear}`;

    transformedData.validationDateString = validationDateString;
  }

  if (transformedData.importedDate) {
    const {
      monthName: importMonthName,
      formattedDay: importFormattedDay,
      year: importYear,
    } = getDateInfo(transformedData.importedDate);
    const importedDateString = `${importFormattedDay} ${importMonthName}, ${importYear}`;

    transformedData.importedDateString = importedDateString;
  }

  return transformedData;
};

function getCurrentDateTimeInUTC(now = new Date()) {
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export const customDateHeader = ({ decreaseMonth, increaseMonth, date }) => {
  const currentMonthName = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(date);

  return (
    <div className=" h-8 flex justify-between items-center">
      <button
        type="button"
        className="ml-2 text-dark-night hover:bg-slate-100 duration-500"
        onClick={decreaseMonth}
      >
        <i className="material-icons !text-[24px] duration-500 hover:!text-orange-500 hover:scale-x-125 hover:scale-y-125">
          chevron_left
        </i>
      </button>
      <p className="text-dark-night text-lg font-bold">{currentMonthName}</p>
      <button
        type="button"
        onClick={increaseMonth}
        className="hover:bg-slate-100 duration-500 mr-2 text-dark-night"
      >
        <i className="material-icons !text-[24px] duration-500 hover:!text-orange-500 hover:scale-x-125 hover:scale-y-125">
          chevron_right
        </i>
      </button>
    </div>
  );
};

export function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function orderSummaryByDate(fileDetails) {
  const dates = [
    {
      time: fileDetails.uploadedDate,
      title: `Uploaded on ${fileDetails.uploadedDateString}`,
      subtitle: `Uploaded By ${capitalize(fileDetails.uploadedBy)}`,
      type: 'upload',
      testData: 'uploaded-right-info',
      component: (
        <img
          src="/assets/images/icons/summary_cloud_upload.svg"
          alt="uploaded"
          className="2xl:w-[48px] w-[24px] h-[48px]"
        />
      ),
    },
    {
      time: fileDetails.validationDate,
      title: ` Validated on ${fileDetails.validationDateString}`,
      subtitle: `Validated By ${capitalize(fileDetails.validationBy)}`,
      type: 'validate',
      testData: 'validate-right-info',
      component: (
        <img
          src="/assets/images/icons/summary_cloud_check.svg"
          alt="validated"
          className="2xl:w-[48px] w-[24px] h-[48px]"
        />
      ),
    },
    {
      time: fileDetails.scheduledImportDate,
      testData: 'schedule-import-right-info',
      title:
        fileDetails.scheduledImportDateString === 'incoming'
          ? 'Not Yet Scheduled For Import'
          : `Scheduled For Import on ${fileDetails.scheduledImportDateString}`,
      subtitle:
        fileDetails.scheduledImportDateString === 'incoming'
          ? ''
          : `Scheduled For Import By ${capitalize(fileDetails.importedBy)}`,
      type: 'scheduled',
      component: (
        <img
          src="/assets/images/icons/summary_calendar_time.svg"
          alt="scheduled import"
          className="2xl:w-[48px] w-[24px] h-[48px]"
        />
      ),
    },
    {
      time: fileDetails.importDate,
      testData: 'imported-right-info',
      title: fileDetails.importDate
        ? `Imported on ${fileDetails.importedDateString}`
        : 'Not Yet Imported',
      subtitle: fileDetails.importDate ? 'Import is now live' : '',
      type: 'import',
      component: (
        <img
          src="/assets/images/icons/summary_transfer_in.svg"
          alt="imported"
          className="2xl:w-[48px] w-[24px] h-[48px]"
        />
      ),
    },
    {
      time: fileDetails.scheduledDeploymentDate,
      testData: 'scheduled-deployment-right-info',
      title: fileDetails.scheduledDeploymentDate
        ? `Scheduled For Deploy on ${fileDetails.scheduledDeploymentDateString}`
        : 'Not Scheduled For Deployment',
      subtitle: fileDetails.scheduledDeploymentDate
        ? `Scheduled for deploy by ${fileDetails.scheduledDeploymentBy}`
        : '',
      type: 'scheduled-deploy',
      component: (
        <img
          src="/assets/images/icons/summary_calendar_time.svg"
          alt="scheduled deploy"
          className="2xl:w-[48px] w-[24px] h-[48px]"
        />
      ),
    },
    {
      time: fileDetails.deployDate,
      testData: 'deployment-right-info',

      title: (() => {
        if (fileDetails.deployDate) {
          if (fileDetails.deployStatus !== 'No devices found') {
            return `Deployed on ${fileDetails.deployDateString}`;
          }

          return 'No Devices Found for Deploy';
        }

        return 'Not Yet Deployed';
      })(),
      subtitle: fileDetails.deployDate ? `Deployed by ${fileDetails.deployedBy}` : '',
      type: 'deploy',
      component: (
        <img
          src="/assets/images/icons/summary_deploy.svg"
          alt="deployed"
          className="2xl:w-[48px] w-[24px] h-[48px]"
        />
      ),
    },
  ];
  const formattedDate = dates.slice().sort((first, second) => {
    const priority = { deploy: 1, import: 2, upload: 3 };
    const a = first.time;
    const b = second.time;

    if (!a && !b) {
      return 0;
    }

    if (!a) {
      return 1;
    }

    if (!b) {
      return -1;
    }

    const retVal = new Date(b) - new Date(a); // most recent first

    if (retVal !== 0) {
      return retVal;
    }

    return (priority[first.type] || 99) - (priority[second.type] || 99);
  });

  return formattedDate;
}

export const dayClassName = date => {
  const day = date.getDay();

  if (day === 0) {
    return 'S';
  }

  if (day === 6) {
    return 'S';
  }

  return 'weekday'; // Default for other days
};

export function insertSpaceAt46th(str) {
  if (str.length < 46) {
    return str;
  }

  return `${str.slice(0, 45)} ${str.slice(45)}`;
}

export const getImportTooltip = el => {
  switch (el.importStatus) {
    case 'imported':
      return `imported on ${el.importedDateString} by ${capitalize(el.importedBy)}`;
    case 'not_imported':
      return 'awaiting import';
    default:
      return `import scheduled for ${el.scheduledImportDateString}`;
  }
};
